/**
 * Auto-colouring for mechanical-layout footprints. Colour is assigned per
 * PART TYPE (template key, else label, else part number) so identical parts
 * share one colour and the legend stays short. Assignment is by the sorted set
 * of distinct types present on the sheet → each distinct type gets the next
 * palette entry, maximising visual separation (no hash collisions).
 */

// Curated, mutually-distinct, mid-tone fills (white text reads on all of them).
const PALETTE = [
  '#2563eb', '#dc2626', '#16a34a', '#d97706', '#7c3aed',
  '#0891b2', '#db2777', '#65a30d', '#ea580c', '#0d9488',
  '#4f46e5', '#b91c1c', '#059669', '#9333ea', '#0284c7',
  '#ca8a04', '#e11d48', '#15803d', '#c2410c', '#6d28d9',
];

function norm(value: unknown): string {
  return typeof value === 'string' ? value.trim().toLowerCase().replace(/\s+/g, ' ') : '';
}

export interface MechPartData {
  templateKey?: unknown;
  label?: string;
  partNumber?: string;
}

/** Stable identity for "same part type". */
export function mechanicalPartKey(data: MechPartData): string {
  if (typeof data.templateKey === 'string' && data.templateKey) return `k:${data.templateKey}`;
  const label = norm(data.label);
  if (label) return `l:${label}`;
  const pn = norm(data.partNumber);
  if (pn) return `p:${pn}`;
  return 'item';
}

export interface MechColorEntry {
  key: string;
  color: string;
  label: string;
  qty: number;
}

/**
 * Build the per-type colour map + legend entries for a set of mechanical nodes.
 * Returns the colour lookup (by part key) and the legend rows (sorted by label).
 */
export function buildMechanicalColors(
  items: Array<{ data: MechPartData }>,
): { colorByKey: Map<string, string>; legend: MechColorEntry[] } {
  const byKey = new Map<string, { label: string; qty: number }>();
  for (const it of items) {
    const key = mechanicalPartKey(it.data);
    const existing = byKey.get(key);
    if (existing) existing.qty += 1;
    else byKey.set(key, { label: (it.data.label ?? '').trim() || 'Mechanical item', qty: 1 });
  }
  // Deterministic order so colours are stable for a given set of part types.
  const keys = [...byKey.keys()].sort();
  const colorByKey = new Map<string, string>();
  keys.forEach((key, i) => colorByKey.set(key, PALETTE[i % PALETTE.length]));

  const legend: MechColorEntry[] = keys
    .map((key) => ({
      key,
      color: colorByKey.get(key)!,
      label: byKey.get(key)!.label,
      qty: byKey.get(key)!.qty,
    }))
    .sort((a, b) => a.label.localeCompare(b.label));

  return { colorByKey, legend };
}
